import React, { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react';
import { styleAPI } from '../api';

const StylesContext = createContext();

export const useStyles = () => {
    const context = useContext(StylesContext);
    if (!context) {
        throw new Error('useStyles must be used within a StylesProvider');
    }
    return context;
};

export const StylesProvider = ({ children }) => {
    const [styles, setStyles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastFetched, setLastFetched] = useState(null);

    // Read current styles/lastFetched through refs so fetchStyles keeps a
    // stable identity and consumers' effects don't re-run on every fetch.
    const stylesRef = useRef(styles);
    stylesRef.current = styles;
    const lastFetchedRef = useRef(lastFetched);
    lastFetchedRef.current = lastFetched;

    const fetchStyles = useCallback(async (force = false) => {
        // If we already have styles and it's been less than 5 minutes, don't refetch
        if (!force && stylesRef.current.length > 0 && lastFetchedRef.current) {
            const fiveMinutes = 5 * 60 * 1000;
            if (Date.now() - lastFetchedRef.current < fiveMinutes) {
                return stylesRef.current;
            }
        }

        setLoading(true);
        setError(null);
        try {
            // Player-facing catalog: request active styles explicitly, since the
            // backend returns every status (draft/deleted...) to moderators.
            const response = await styleAPI.getStyles({ status: 'active' });
            const stylesData = response.data?.results || response.data || [];
            setStyles(Array.isArray(stylesData) ? stylesData : []);
            setLastFetched(Date.now());
            return stylesData;
        } catch (err) {
            console.error('Failed to fetch styles:', err);
            setError(err);
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    const refreshStyles = useCallback(() => {
        return fetchStyles(true);
    }, [fetchStyles]);

    const toggleFavoriteStyle = useCallback(async (styleId) => {
        const response = await styleAPI.toggleFavorite(styleId);
        const isFavorite = !!response.data?.is_favorite;
        setStyles(prev => prev.map(s => (
            s.id === styleId ? { ...s, is_favorite: isFavorite } : s
        )));
        return isFavorite;
    }, []);

    const value = useMemo(() => ({
        styles,
        loading,
        error,
        fetchStyles,
        refreshStyles,
        toggleFavoriteStyle,
        lastFetched
    }), [styles, loading, error, fetchStyles, refreshStyles, toggleFavoriteStyle, lastFetched]);

    return (
        <StylesContext.Provider value={value}>
            {children}
        </StylesContext.Provider>
    );
};
